import React from "react";
import { motion } from "framer-motion";
import { Target, Send, CalendarCheck } from "lucide-react";

const steps = [
  {
    n: "01",
    icon: Target,
    title: "Deep ICP research",
    body: "We profile your highest-fit accounts from first principles: firmographic, technographic and intent signals; then build a living target list, not a stale CSV.",
  },
  {
    n: "02",
    icon: Send,
    title: "BANT-aligned outreach",
    body: "Multi-channel sequences with copy tuned per persona, qualifying for Budget, Authority, Need and Timing before they ever reach you.",
  },
  {
    n: "03",
    icon: CalendarCheck,
    title: "Calendar drop",
    body: "Only high-intent prospects land on your reps' calendars, with pre-call notes, BANT summary and conversation hooks attached. You show up to close.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="mb-16 max-w-4xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-4">
            How it works
          </p>
          <h2 className="font-editorial font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
            Three moves between
            <br />
            cold market &amp; <span className="italic">closed revenue.</span>
          </h2>
          <p className="mt-6 max-w-2xl text-[#9CA3AF] text-base sm:text-lg">
            We own the engine end-to-end. You own the close. No tools to learn,
            no SDRs to hire.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              data-testid={`step-${s.n}`}
              className="relative bg-[#151821] border border-[#2A2E3D] rounded-3xl p-8 md:p-10 hover:border-[#FF4500]/40 hover:-translate-y-1 transition-all duration-500"
            >
              <div className="flex items-start justify-between mb-10">
                <span className="font-editorial italic text-7xl text-[#FF4500] leading-none">
                  {s.n}
                </span>
                <s.icon className="w-7 h-7 text-[#9CA3AF]" strokeWidth={1.4} />
              </div>
              <h3 className="font-editorial text-3xl text-[#EDEDED] mb-4 tracking-tight">
                {s.title}
              </h3>
              <p className="text-[#9CA3AF] leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
